/**
 * 配信詳細の宛先一覧カード（設計書 9.1 / html 図3・図4・図7）。
 *
 * 設計書: `docs/design-docs/deliveries/open-click-analytics.md`。
 * 本文中の `設計書 N.M` はこの md、`html N.M` / `html 図N` は同ディレクトリの
 * `open-click-analytics.html` を指す。
 */
import { TabButton } from "components/TabButton";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import {
  ATTENTION_COLOR,
  COMPLAINT_MESSAGE,
  DEAD_ROW_BACKGROUND,
  DEAD_ROW_BORDER,
  DELIVERY_STATE_LABEL,
  NO_DATA_COLOR,
  REACTION_SCALE,
} from "constants/delivery-analytics";
import {
  type DeliveryRecipient,
  type RecipientFilter,
  type RecipientSort,
  useDeliveryRecipientsApi,
} from "hooks/use-delivery-api";
import { cn } from "lib/utils";
import { Flag } from "lucide-react";
import { type FC, useState } from "react";
import {
  isActionRequired,
  reactionLabel,
  reactionStep,
  undeliveredMessage,
} from "utils/delivery-analytics";
import { formatRelativeDayTime } from "utils/relative-time";

const PAGE_SIZE = 50;

const SORT_OPTIONS: ReadonlyArray<{ value: RecipientSort; label: string }> = [
  { value: "reaction", label: "反応の強い順" },
  { value: "name", label: "会社名順" },
];

const FILTER_OPTIONS: ReadonlyArray<{
  value: RecipientFilter;
  label: string;
}> = [
  { value: "all", label: "すべて" },
  { value: "undelivered", label: "届かなかった宛先のみ" },
];

type DeliveryRecipientsCardProps = {
  deliveryId: string;
  /** 送信者控えの判定に使う送信元アドレス */
  fromEmail: string;
  filter: RecipientFilter;
  onFilterChange: (next: RecipientFilter) => void;
};

/**
 * 配信詳細の宛先一覧（反応列・最終反応列を追加。設計書 9.1 / html 図3・図4・図7）。
 * @param {DeliveryRecipientsCardProps} props 配信 ID と絞り込み状態
 * @return {JSX.Element} カード
 */
export const DeliveryRecipientsCard: FC<DeliveryRecipientsCardProps> = ({
  deliveryId,
  fromEmail,
  filter,
  onFilterChange,
}) => {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<RecipientSort>("reaction");

  // 不達チップ経由でも絞り込みタブ経由でも同じようにページを戻す
  // （2 ページ目を見ている状態でチップを押すと空の一覧になるため）。
  // レンダー中に前回値と比べる React 公式のやり方で、effect の 1 往復を挟まない。
  const [lastFilter, setLastFilter] = useState<RecipientFilter>(filter);
  if (lastFilter !== filter) {
    setLastFilter(filter);
    setPage(1);
  }
  const { data, loading } = useDeliveryRecipientsApi({
    deliveryId,
    page,
    limit: PAGE_SIZE,
    sort,
    filter,
  });

  // 実際に適用された並び順（measured=false では reaction → name に縮退する）
  const appliedSort = data?.sort ?? sort;
  const measured = data?.measured ?? true;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="card bg-base-100 shadow-md">
      <div className="card-body">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="card-title text-base">
            宛先一覧 ({data?.total ?? 0}件)
          </h3>
          <div className="flex items-center gap-3">
            <div className="flex gap-4 border-b border-slate-200">
              {FILTER_OPTIONS.map(option => (
                // 一覧の絞り込みは「いま見ている 1 つ」を選ぶタブなので
                // aria-pressed は付けない
                <TabButton
                  key={option.value}
                  active={option.value === filter}
                  onClick={() => onFilterChange(option.value)}
                >
                  {option.label}
                </TabButton>
              ))}
            </div>
            <label className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs">
              並び替え:
              <select
                value={appliedSort}
                onChange={event => {
                  setSort(event.target.value as RecipientSort);
                  setPage(1);
                }}
                className="bg-transparent font-semibold outline-none"
              >
                {SORT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>会社名</th>
                <th>担当者</th>
                <th>メール</th>
                <th>
                  反応
                  {!measured && (
                    <div className="text-[10px] font-normal text-base-content/60">
                      開封・クリックは計測していません
                    </div>
                  )}
                </th>
                <th className="whitespace-nowrap">最終反応</th>
                <th className="whitespace-nowrap">状態</th>
                <th className="whitespace-nowrap">送信日時</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8">
                    <Loading size="md" />
                  </td>
                </tr>
              ) : !data?.items.length ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-8 text-base-content/60"
                  >
                    宛先がありません
                  </td>
                </tr>
              ) : (
                data.items.map(recipient => (
                  <RecipientRow
                    key={recipient.id}
                    recipient={recipient}
                    measured={measured}
                    isSenderCopy={
                      !recipient.companyName && recipient.email === fromEmail
                    }
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center mt-4">
            <div className="join">
              <Button
                type="button"
                size="sm"
                isJoinItem
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                &laquo;
              </Button>
              <Button type="button" size="sm" isJoinItem>
                {page} / {totalPages}
              </Button>
              <Button
                type="button"
                size="sm"
                isJoinItem
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                &raquo;
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

type RecipientRowProps = {
  recipient: DeliveryRecipient;
  measured: boolean;
  isSenderCopy: boolean;
};

/**
 * 宛先一覧の 1 行。届いていない宛先だけ行ごとグレーにする。
 * @param {RecipientRowProps} props 宛先・計測フラグ
 * @return {JSX.Element} 行
 */
const RecipientRow: FC<RecipientRowProps> = ({
  recipient,
  measured,
  isSenderCopy,
}) => {
  const isDead =
    recipient.deliveryState === "undelivered" ||
    recipient.deliveryState === "failed";
  // まだ送信を試みていない宛先は反応を持たない（下書き・予約中の配信）
  const isPending = recipient.deliveryState === "pending";
  const actionRequired = isActionRequired(recipient.actionCategory);
  const step = reactionStep(recipient);
  const tint = isDead ? DEAD_ROW_BACKGROUND : REACTION_SCALE[step].tint;

  const leadBorder = isDead
    ? actionRequired
      ? ATTENTION_COLOR
      : DEAD_ROW_BORDER
    : REACTION_SCALE[step].color;

  const message = undeliveredMessage({
    deliveryState: recipient.deliveryState,
    bounceType: recipient.bounceType,
    detailType: recipient.detailType,
    errorMessage: recipient.errorMessage,
  });

  const rowStyle = {
    background: tint ?? undefined,
    color: isDead ? NO_DATA_COLOR : undefined,
  };

  return (
    <tr style={rowStyle}>
      <td
        style={{ borderLeft: `3px solid ${leadBorder}` }}
        className={cn(actionRequired && "font-bold")}
      >
        {actionRequired && (
          <Flag
            size={12}
            aria-hidden
            className="mr-1 inline"
            style={{ color: ATTENTION_COLOR }}
          />
        )}
        {isSenderCopy ? (
          <span className="text-xs" style={{ color: NO_DATA_COLOR }}>
            送信者控え
          </span>
        ) : (
          recipient.companyName || "-"
        )}
      </td>
      <td>{recipient.name || "—"}</td>
      <td className="text-sm">{recipient.email}</td>
      {isDead ? (
        <td colSpan={2} className="text-xs">
          {message}
        </td>
      ) : (
        <>
          <td>
            <ReactionBar
              recipient={recipient}
              measured={measured}
              pending={isPending}
            />
          </td>
          <td className="text-sm">
            <LastReaction
              recipient={recipient}
              measured={measured}
              pending={isPending}
            />
            {recipient.deliveryState === "complained" && (
              <div className="text-[10.5px]" style={{ color: NO_DATA_COLOR }}>
                {COMPLAINT_MESSAGE}
              </div>
            )}
          </td>
        </>
      )}
      <td className="whitespace-nowrap">
        {recipient.deliveryState === "complained" ? (
          <span style={{ color: NO_DATA_COLOR }}>
            {DELIVERY_STATE_LABEL.complained}
          </span>
        ) : (
          DELIVERY_STATE_LABEL[recipient.deliveryState]
        )}
      </td>
      <td className="whitespace-nowrap text-sm">
        {formatRelativeDayTime(recipient.sentAt)}
      </td>
    </tr>
  );
};

type ReactionCellProps = {
  recipient: DeliveryRecipient;
  measured: boolean;
  /** まだ送信を試みていない宛先（下書き・予約中の配信）か */
  pending: boolean;
};

/**
 * 値が無い / 計測対象外を示す `—`。
 *
 * 「何も無いこと」の見せ方は 1 か所に閉じる（色や記号を変えるときに、
 * 一覧の中で 1 セルだけ取り残されるのを防ぐため）。
 * @return {JSX.Element} `—`
 */
const NoData: FC = () => <span style={{ color: NO_DATA_COLOR }}>—</span>;

/**
 * 反応列の 6 段バー（長さとラベル文字で強さを二重に符号化する）。
 * 計測対象外の配信では返信のみに縮退し、まだ送っていない宛先は `—` にする。
 * @param {ReactionCellProps} props 宛先・計測フラグ・未送信フラグ
 * @return {JSX.Element} 反応バー
 */
const ReactionBar: FC<ReactionCellProps> = ({
  recipient,
  measured,
  pending,
}) => {
  if (pending || (!measured && !recipient.replied)) {
    return <NoData />;
  }
  const step = reactionStep(recipient);
  const token = REACTION_SCALE[step];
  return (
    <div className="relative h-4 overflow-hidden rounded-[3px] bg-slate-100">
      <i
        className="absolute inset-y-0 left-0 block rounded-[3px]"
        style={{ width: `${token.width}%`, background: token.color }}
      />
      <b className="relative inline-flex items-center gap-1 pl-1.5 text-[10px] font-semibold leading-4 text-slate-800">
        {step === 6 && <ReplyIcon />}
        {step === 1 && <NoReactionIcon />}
        {reactionLabel(recipient)}
      </b>
    </div>
  );
};

/**
 * 反応バー最上段（返信）のアイコン。ラベル文字と同じ色で描く。
 * @return {JSX.Element} アイコン
 */
const ReplyIcon: FC = () => (
  <svg
    viewBox="0 -960 960 960"
    width={16}
    height={16}
    fill="currentColor"
    aria-hidden
    className="shrink-0"
  >
    <title>返信</title>
    <path d="M240-400q0 52 21 98.5t60 81.5q-1-5-1-9v-9q0-32 12-60t35-51l113-111 113 111q23 23 35 51t12 60v9q0 4-1 9 39-35 60-81.5t21-98.5q0-50-18.5-94.5T648-574q-20 13-42 19.5t-45 6.5q-62 0-107.5-41T401-690q-39 33-69 68.5t-50.5 72Q261-513 250.5-475T240-400Zm240 52-57 56q-11 11-17 25t-6 29q0 32 23.5 55t56.5 23q33 0 56.5-23t23.5-55q0-16-6-29.5T537-292l-57-56Zm0-492v132q0 34 23.5 57t57.5 23q18 0 33.5-7.5T622-658l18-22q74 42 117 117t43 163q0 134-93 227T480-80q-134 0-227-93t-93-227q0-129 86.5-245T480-840Z" />
  </svg>
);

/**
 * 反応バー最下段（反応なし）のアイコン。ラベル文字と同じ色で描く。
 * @return {JSX.Element} アイコン
 */
const NoReactionIcon: FC = () => (
  <svg
    viewBox="0 -960 960 960"
    width={16}
    height={16}
    fill="currentColor"
    aria-hidden
    className="shrink-0"
  >
    <title>反応なし</title>
    <path d="M440-246 337-145q-11 11-27.5 11T282-146q-12-11-12-27.5t12-28.5l158-158v-80h-80L201-281q-11 11-27.5 11T145-282q-11-11-11-27.5t11-27.5l101-103H119q-17 0-28-11.5T80-480q0-17 11.5-28.5T120-520h126L145-622q-11-11-11-27.5t12-28.5q11-11 27.5-11t28.5 11l158 158h80v-80L281-758q-11-11-11-27.5t12-28.5q11-11 27.5-11t27.5 11l103 100v-126q0-17 11.5-28.5T480-880q17 0 28.5 11.5T520-840v126l102-100q11-11 27.5-11t28.5 11q11 12 11 28.5T678-758L520-600v80h80l158-158q11-11 27.5-11t28.5 12q11 11 11 27.5T814-622L714-520h126q17 0 28.5 11.5T880-480q0 17-11.5 28.5T840-440H714l100 103q11 11 11 27.5T814-282q-12 12-28.5 12T758-282L600-440h-80v80l158 159q11 11 11 27.5T677-145q-11 11-27.5 11T622-145L520-246v127q0 17-11.5 28T480-80q-17 0-28.5-11.5T440-120v-126Z" />
  </svg>
);

/**
 * 最終反応列（計測対象外では返信のあった宛先だけ時刻を出す）。
 * まだ送っていない宛先は `—` にする。
 * @param {ReactionCellProps} props 宛先・計測フラグ・未送信フラグ
 * @return {JSX.Element} 最終反応
 */
const LastReaction: FC<ReactionCellProps> = ({
  recipient,
  measured,
  pending,
}) => {
  if (
    pending ||
    !recipient.lastReactionAt ||
    (!measured && !recipient.replied)
  ) {
    return <NoData />;
  }
  return <>{formatRelativeDayTime(recipient.lastReactionAt)}</>;
};
